import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  InputAdornment, 
  IconButton, 
  Alert, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Popper, 
  ClickAwayListener 
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
// import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { UniversalMapProps, LatLng, UniversalMapEntry } from './MapInterface';
import 'mapbox-gl/dist/mapbox-gl.css';
// import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

interface MapboxMapComponentProps extends UniversalMapProps {}

const MapboxMapComponent = forwardRef<HTMLDivElement, MapboxMapComponentProps>(({
  center,
  zoom,
  className,
  style,
  onMapClick,
  onLocationSearch,
  onMarkerClick,
  markers = [],
  entries = [],
  route,
  showSearch = true,
  showControls = true,
  interactive = true,
  ...props
}, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasCenteredRef = useRef<boolean>(false); // Track if we've already centered the map
  const mapInitializedRef = useRef<boolean>(false); // Track if map has been initialized
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentMarkers, setCurrentMarkers] = useState<mapboxgl.Marker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [navigationControl, setNavigationControl] = useState<mapboxgl.NavigationControl | null>(null);
  const [fullscreenControl, setFullscreenControl] = useState<mapboxgl.FullscreenControl | null>(null);

  // Initialize Mapbox map
  useEffect(() => {
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    
    if (!accessToken) {
      setError('Mapbox access token is not configured');
      return;
    }

    // Prevent multiple initializations with a ref check
    if (mapContainer.current && !map && !mapInitializedRef.current) {
      console.log('Initializing Mapbox map for the first time');
      mapInitializedRef.current = true; // Mark as initializing immediately
      
      mapboxgl.accessToken = accessToken;

      try {
        // Clear the container to prevent Mapbox warnings about non-empty containers
        if (mapContainer.current) {
          mapContainer.current.innerHTML = '';
        }

        const newMap = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [center.lng, center.lat],
          zoom: zoom,
          attributionControl: showControls,
          interactive: interactive,
        });

        // Add map controls if enabled
        if (showControls) {
          const navControl = new mapboxgl.NavigationControl();
          const fullControl = new mapboxgl.FullscreenControl();
          newMap.addControl(navControl, 'top-right');
          newMap.addControl(fullControl, 'top-right');
          setNavigationControl(navControl);
          setFullscreenControl(fullControl);
        }

        // Add click listener if provided
        if (onMapClick) {
          newMap.on('click', (e) => {
            onMapClick({
              lat: e.lngLat.lat,
              lng: e.lngLat.lng,
            });
          });
        }

        // Initialize map search functionality
        if (showSearch) {
          // We'll implement search using the Mapbox Geocoding API directly
          // This approach avoids the geocoder dependency for now
        }

        newMap.on('load', () => {
          console.log('Mapbox map loaded and ready (SINGLE LOAD)');
          setMap(newMap);
        });

        newMap.on('styledata', () => {
          console.log('Mapbox style loaded and ready (SINGLE STYLE)');
          // The style is now fully loaded, safe to add markers and center
          // Manually trigger marker processing after a short delay
          setTimeout(() => {
            console.log('Style ready - manually checking for markers and entries');
            console.log('Current entries:', entries);
            console.log('Current markers:', markers);
          }, 100);
        });

        newMap.on('error', (e) => {
          console.error('Mapbox error:', e);
          setError(`Mapbox error: ${e.error?.message || 'Unknown error'}`);
        });

      } catch (err) {
        console.error('Failed to initialize Mapbox:', err);
        setError(`Failed to initialize Mapbox: ${err instanceof Error ? err.message : 'Unknown error'}`);
        mapInitializedRef.current = false; // Reset on error
      }
    }

    // Cleanup function
    return () => {
      if (map) {
        console.log('Cleaning up Mapbox map');
        
        // Clear markers first
        currentMarkers.forEach(marker => marker.remove());
        setCurrentMarkers([]);
        
        // Remove controls
        if (navigationControl) {
          map.removeControl(navigationControl);
          setNavigationControl(null);
        }
        if (fullscreenControl) {
          map.removeControl(fullscreenControl);
          setFullscreenControl(null);
        }
        
        // Remove the map
        map.remove();
        setMap(null);
        mapInitializedRef.current = false; // Reset initialization flag
        hasCenteredRef.current = false; // Reset centering flag
      }
    };
  }, []); // Empty dependency array to prevent re-initialization

  // Update map center when prop changes
  useEffect(() => {
    if (map) {
      map.setCenter([center.lng, center.lat]);
    }
  }, [map, center]);

  // Update map zoom when prop changes
  useEffect(() => {
    if (map) {
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  // Update map controls and interactivity when props change
  useEffect(() => {
    if (map) {
      // Handle controls
      if (showControls) {
        // Add controls if they don't exist
        if (!navigationControl) {
          const navControl = new mapboxgl.NavigationControl();
          map.addControl(navControl, 'top-right');
          setNavigationControl(navControl);
        }
        if (!fullscreenControl) {
          const fullControl = new mapboxgl.FullscreenControl();
          map.addControl(fullControl, 'top-right');
          setFullscreenControl(fullControl);
        }
      } else {
        // Remove controls if they exist
        if (navigationControl) {
          map.removeControl(navigationControl);
          setNavigationControl(null);
        }
        if (fullscreenControl) {
          map.removeControl(fullscreenControl);
          setFullscreenControl(null);
        }
      }

      // Update interactivity
      if (interactive) {
        map.scrollZoom.enable();
        map.boxZoom.enable();
        map.dragRotate.enable();
        map.dragPan.enable();
        map.keyboard.enable();
        map.doubleClickZoom.enable();
        map.touchZoomRotate.enable();
      } else {
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.dragRotate.disable();
        map.dragPan.disable();
        map.keyboard.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();
      }
    }
  }, [map, showControls, interactive, navigationControl, fullscreenControl]);

  // Add search functionality when both map and search are ready
  // For now, we'll implement a simple search placeholder
  // In Phase 3, we can add the full Mapbox Geocoding API integration

  // Update markers when props change
  useEffect(() => {
    if (map && map.isStyleLoaded()) {
      console.log('Mapbox: Updating markers, entries:', entries, 'markers:', markers); // Debug log
      
      // Reset centering flag when entries change (e.g., navigating to different profile)
      hasCenteredRef.current = false;
      
      // Clear existing markers
      currentMarkers.forEach(marker => marker.remove());
      const newMarkers: mapboxgl.Marker[] = [];

      // Add temporary markers (from map clicks or search)
      markers.forEach((markerInfo) => {
        console.log('Adding temp marker:', markerInfo); // Debug log
        
        // Create marker element with better scaling properties
        const markerElement = document.createElement('div');
        markerElement.style.width = '32px';
        markerElement.style.height = '32px';
        markerElement.style.backgroundImage = 'url(/map_pin.png)';
        markerElement.style.backgroundSize = '100% 100%';
        markerElement.style.backgroundRepeat = 'no-repeat';
        markerElement.style.cursor = 'pointer';
        // Important: Don't use innerHTML with img tags as they can cause scaling issues

        const marker = new mapboxgl.Marker(markerElement, { 
          anchor: 'bottom', // Anchor at the bottom of the pin (where it points)
          offset: [0, 0] // No additional offset needed with proper anchor
        })
          .setLngLat([markerInfo.position.lng, markerInfo.position.lat])
          .setPopup(new mapboxgl.Popup().setHTML(`<h3>${markerInfo.title}</h3>`))
          .addTo(map);

        newMarkers.push(marker);
      });

      // Add entry markers
      entries.forEach((entry) => {
        console.log('Adding entry marker:', entry.title, 'at:', entry.location); // Debug log
        
        // Check if coordinates are valid
        if (!entry.location.lat || !entry.location.lng || 
            isNaN(entry.location.lat) || isNaN(entry.location.lng)) {
          console.warn('Invalid coordinates for entry:', entry.title, entry.location);
          return;
        }
        
        console.log('Valid coordinates for:', entry.title, `lat: ${entry.location.lat}, lng: ${entry.location.lng}`); // Debug coordinates
        
        // Create marker element with better scaling properties
        const markerElement = document.createElement('div');
        markerElement.style.width = '32px';
        markerElement.style.height = '32px';
        markerElement.style.backgroundImage = 'url(/map_pin.png)';
        markerElement.style.backgroundSize = '100% 100%';
        markerElement.style.backgroundRepeat = 'no-repeat';
        markerElement.style.cursor = 'pointer';
        // Important: Don't use innerHTML with img tags as they can cause scaling issues

        const popupContent = `
          <div>
            <h3>${entry.title}</h3>
            ${entry.content ? `<p>${entry.content.substring(0, 100)}${entry.content.length > 100 ? '...' : ''}</p>` : ''}
            ${entry.date ? `<small>${new Date(entry.date).toLocaleDateString()}</small>` : ''}
          </div>
        `;

        try {
          const marker = new mapboxgl.Marker(markerElement, { 
            anchor: 'bottom', // Anchor at the bottom of the pin (where it points)
            offset: [0, 0] // No additional offset needed with proper anchor
          })
            .setLngLat([entry.location.lng, entry.location.lat])
            .setPopup(new mapboxgl.Popup().setHTML(popupContent))
            .addTo(map);

          // Add click handler for entry markers
          if (onMarkerClick) {
            markerElement.addEventListener('click', () => {
              onMarkerClick(entry);
            });
          }

          newMarkers.push(marker);
          console.log('Successfully added marker for:', entry.title); // Debug log
        } catch (error) {
          console.error('Error adding marker for entry:', entry.title, error);
        }
      });

      setCurrentMarkers(newMarkers);
      console.log('Total markers added:', newMarkers.length); // Debug log
    }
  }, [map, markers, entries, onMarkerClick]);

  // Separate effect for auto-centering to prevent multiple calls
  useEffect(() => {
    if (map && map.isStyleLoaded() && entries.length > 0 && !hasCenteredRef.current) {
      // Add a longer delay to ensure everything is fully ready
      const centerTimeout = setTimeout(() => {
        try {
          const validEntries = entries.filter(entry => 
            entry.location.lat && entry.location.lng && 
            !isNaN(entry.location.lat) && !isNaN(entry.location.lng)
          );
          
          if (validEntries.length > 0) {
            if (validEntries.length === 1) {
              // For single entry, just center on it
              const entry = validEntries[0];
              console.log('Auto-centering on single entry (first time):', entry.title, entry.location);
              
              // Use setCenter and setZoom instead of flyTo for more reliable positioning
              map.setCenter([entry.location.lng, entry.location.lat]);
              map.setZoom(12);
              
              hasCenteredRef.current = true; // Mark as centered to prevent future calls
              console.log('Map centered on single entry successfully');
            } else {
              // For multiple entries, fit to bounds
              console.log('Auto-fitting bounds for multiple entries:', validEntries.length);
              const bounds = new mapboxgl.LngLatBounds();
              
              validEntries.forEach((entry) => {
                bounds.extend([entry.location.lng, entry.location.lat]);
                console.log('Added to bounds:', entry.title, [entry.location.lng, entry.location.lat]);
              });
              
              // Fit the map to the bounds with some padding
              if (!bounds.isEmpty()) {
                map.fitBounds(bounds, {
                  padding: 50,
                  maxZoom: 12 // Don't zoom in too much
                });
                console.log('Map fitted to bounds with', validEntries.length, 'entries');
                hasCenteredRef.current = true; // Mark as centered to prevent future calls
              }
            }
          }
        } catch (error) {
          console.error('Error auto-centering map:', error);
        }
      }, 500); // Longer delay to ensure complete initialization

      return () => clearTimeout(centerTimeout);
    }
  }, [map, entries.length]); // Only depend on map and number of entries

  // Route rendering effect
  useEffect(() => {
    if (!map || !route || !route.geometry || route.geometry.length === 0) {
      // Remove existing route if no route data
      if (map && map.getSource('route')) {
        map.removeLayer('route');
        map.removeSource('route');
      }
      return;
    }

    // Wait for map to be fully loaded
    const addRoute = () => {
      try {
        // Remove existing route
        if (map.getSource('route')) {
          map.removeLayer('route');
          map.removeSource('route');
        }

        // Convert our LatLng geometry to GeoJSON format
        const routeGeoJSON = {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: route.geometry.map(point => [point.lng, point.lat])
          }
        };

        console.log('Adding route to map:', routeGeoJSON);

        // Add route source
        map.addSource('route', {
          type: 'geojson',
          data: routeGeoJSON
        });

        // Add route layer
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#3b82f6', // Blue color
            'line-width': 4,
            'line-opacity': 0.8
          }
        });

        console.log('Route added successfully');
      } catch (error) {
        console.error('Error adding route to map:', error);
      }
    };

    if (map.isStyleLoaded()) {
      addRoute();
    } else {
      const cleanup = () => {
        map.off('styledata', addRoute);
      };
      map.on('styledata', addRoute);
      return cleanup;
    }
  }, [map, route]);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
  }, []);

  const fetchSearchSuggestions = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&access_token=${accessToken}&session_token=${Date.now()}&limit=5&types=poi%2Caddress%2Cplace&language=en&country=US`
      );
      const data = await response.json();
      
      if (data.suggestions && data.suggestions.length > 0) {
        setSearchSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleSuggestionSelect = useCallback(async (suggestion: any) => {
    try {
      const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      
      // Get detailed place information using the mapbox_id
      const detailResponse = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}?access_token=${accessToken}&session_token=${Date.now()}`
      );
      const detailData = await detailResponse.json();
      
      if (detailData.features && detailData.features.length > 0) {
        const feature = detailData.features[0];
        const location = {
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0]
        };
        
        // Pan and zoom to the location with smooth animation
        map?.flyTo({
          center: [location.lng, location.lat],
          zoom: 14,
          duration: 2000 // 2 second animation
        });
        
        if (onLocationSearch) {
          // Use the full name from the suggestion
          const placeName = suggestion.name || suggestion.full_address || 'Unknown Location';
          onLocationSearch(location, placeName);
        }
        
        setSearchValue(suggestion.name || suggestion.full_address || '');
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error selecting suggestion:', error);
    }
  }, [map, onLocationSearch]);

  const handleSearchSubmit = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || !map) return;

    try {
      console.log('🔍 MapboxMap: Starting two-step search for:', searchQuery);
      
      // Use our two-step search API instead of expensive Mapbox API
      const response = await fetch(`/api/searchPlaces?query=${encodeURIComponent(searchQuery)}&debug=true`);
      const data = await response.json();
      
      console.log('🔍 MapboxMap: Two-step search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.lat,
          lng: result.lng
        };
        
        console.log('🔍 MapboxMap: Found location:', location, 'from provider:', result.provider);
        
        // Pan and zoom to the location with smooth animation
        map.flyTo({
          center: [location.lng, location.lat],
          zoom: 14,
          duration: 2000 // 2 second animation
        });
        
        if (onLocationSearch) {
          // Use the business name or formatted address
          const placeName = result.name || result.display_name || 'Unknown Location';
          onLocationSearch(location, placeName);
        }
        setSearchValue('');
      } else {
        console.warn('🔍 MapboxMap: No results found for search query:', searchQuery);
        console.warn('🔍 MapboxMap: Search response:', data);
      }
    } catch (error) {
      console.error('🔍 MapboxMap: Error in two-step search:', error);
    }
  }, [map, onLocationSearch]);

  const handleGetCurrentLocation = useCallback(() => {
    if (navigator.geolocation && map) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          // Pan and zoom to current location with smooth animation
          map.flyTo({
            center: [location.lng, location.lat],
            zoom: 15,
            duration: 2000 // 2 second animation
          });
          
          if (onLocationSearch) {
            onLocationSearch(location, 'Current Location');
          }
        },
        (error) => {
          console.error('Error getting current location:', error);
        }
      );
    }
  }, [map, onLocationSearch]);

  if (error) {
    return (
      <Box 
        ref={ref}
        className={className}
        style={style}
        sx={{ p: 2 }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      ref={ref}
      className={className}
      style={style}
      sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}
    >
      {showSearch && (
        <Box
          ref={searchContainerRef}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search for businesses, places, or addresses..."
                value={searchValue}
                inputRef={searchInputRef}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  // Fetch suggestions as user types
                  fetchSearchSuggestions(value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit(searchValue);
                    setShowSuggestions(false);
                  }
                }}
                size="small"
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          handleSearchSubmit(searchValue);
                          setShowSuggestions(false);
                        }}
                        disabled={!searchValue.trim()}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchValue && (
                        <IconButton size="small" onClick={handleSearchClear}>
                          <ClearIcon />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={handleGetCurrentLocation}>
                        <MyLocationIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Search suggestions dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    maxHeight: 200,
                    overflow: 'auto',
                    zIndex: 1001,
                  }}
                >
                  <List dense>
                    {searchSuggestions.map((suggestion, index) => (
                      <ListItem
                        key={index}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          primary={suggestion.name || suggestion.full_address}
                          secondary={suggestion.place_formatted || suggestion.context?.place?.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      )}
      
      <Box
        ref={mapContainer}
        sx={{ width: '100%', height: '100%', minHeight: 400 }}
      />
    </Box>
  );
});

MapboxMapComponent.displayName = 'MapboxMapComponent';

export default MapboxMapComponent;